import { motion } from 'framer-motion';
import { useState } from 'react';

const Portfolio = () => {
  const [activeApp, setActiveApp] = useState<number | null>(null);

  const portfolioApps = [
    {
      id: 1,
      title: "The Off-Roster",
      subtitle: "TALENT MGMT PLATFORM",
      description: "Our network of nationwide freelance directors all housed in one feature rich platform made to be their megaphone. Coming soon.",
      tech: ["React", "TypeScript", "Firebase", "Claude", "Vercel"],
      status: "BETA COMING SOON",
      link: "https://on.theoffroster.com",
      image: "/tor.png", // Placeholder
      video: "/placeholder-video-1.mp4" // Placeholder
    },
    {
      id: 2,
      title: "PostSoup",
      subtitle: "Post Production Asset Management System",
      description: "Custom-built project management system specifically for post-production. Using a Figma-like canvas and node based assets, users can plan, track, schedule and even export their projects.",
      tech: ["React", "Node.js", "Firebase", "Claude", "Vercel"],
      status: "CONCEPT",
      link: "https://eat.postsoup.io/sandbox",
      image: "/postsoup.png", // Placeholder
      video: "/placeholder-video-2.mp4" // Placeholder
    },
    {
      id: 3,
      title: "QuicKey",
      subtitle: "KEYBOARD SHORTCUT TRAINER",
      description: "A tool that helps you learn keyboard shortcuts for media programs. It's a fun and engaging way to learn keyboard shortcuts.",
      tech: ["React", "TypeScript", "Firebase", "Claude", "Vercel"],
      status: "LIVE",
      link: "https://quickey.io",
      image: "/quickey.png", // Placeholder
      video: "/placeholder-video-3.mp4" // Placeholder
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'LIVE': return 'bg-green-500';
      case 'BETA': return 'bg-yellow-500';
      case 'BETA COMING SOON': return 'bg-orange-500';
      case 'CONCEPT': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="h-screen bg-transparent text-white overflow-hidden md:overflow-hidden overflow-y-auto">
      {/* Main Container */}
      <div className="h-full px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        
        {/* Header */}
        <motion.div
          className="mb-8 lg:mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative max-w-fit">
            <div className="absolute top-3 left-3 w-full h-full bg-white/10"></div>
            <div className="relative bg-black border-4 border-white p-6 lg:p-8 transform -rotate-1">
              <h1 style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }} className="font-black uppercase leading-none">
                PORTFOLIO
              </h1>
              <div className="w-16 lg:w-24 h-2 bg-white mt-3"></div>
            </div>
          </div>
        </motion.div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {portfolioApps.map((app, index) => (
            <motion.div
              key={app.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onMouseEnter={() => setActiveApp(index)}
              onMouseLeave={() => setActiveApp(null)}
              onClick={() => window.open(app.link, '_blank')}
            >
              {/* App Card */}
              <div className="relative h-full min-h-[500px] lg:min-h-[600px]">
                {/* Background shadow */}
                <div className="absolute top-4 left-4 w-full h-full bg-white"></div>
                
                {/* Main card */}
                <div className={`relative bg-black border-4 border-white h-full transition-transform duration-200 ${
                  activeApp === index ? 'transform -translate-x-2 -translate-y-2' : ''
                }`}>
                  
                  {/* Media Section */}
                  <div className="relative h-48 lg:h-56 bg-gray-800 border-b-4 border-white overflow-hidden">
                    {/* Image/Video Display */}
                    {app.image && app.image !== '/placeholder-app-1.jpg' && app.image !== '/placeholder-app-2.jpg' && app.image !== '/placeholder-app-3.jpg' ? (
                      <img 
                        src={app.image} 
                        alt={app.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    
                    {/* Placeholder for missing images */}
                    <div className={`w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center ${
                      app.image && app.image !== '/placeholder-app-1.jpg' && app.image !== '/placeholder-app-2.jpg' && app.image !== '/placeholder-app-3.jpg' ? 'hidden' : ''
                    }`}>
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white/20 mx-auto mb-3 transform rotate-45"></div>
                        <p className="text-white/60 font-mono text-sm">
                          {activeApp === index ? 'VIDEO' : 'IMAGE'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      <div className={`${getStatusColor(app.status)} text-black px-3 py-1 font-black text-xs uppercase border-2 border-white`}>
                        {app.status}
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-4 lg:p-6 space-y-4">
                    {/* Title Block */}
                    <div className="bg-white text-black p-3 border-2 border-white transform rotate-1">
                      <h3 style={{ fontSize: 'clamp(1rem, 1.5vw, 1.25rem)' }} className="font-black uppercase tracking-wide">
                        {app.title}
                      </h3>
                      <p className="text-xs font-bold uppercase tracking-widest opacity-70">
                        {app.subtitle}
                      </p>
                    </div>

                    {/* Description */}
                    <p style={{ fontSize: 'clamp(0.75rem, 1vw, 0.875rem)' }} className="text-white leading-relaxed font-medium">
                      {app.description}
                    </p>

                    {/* Tech Stack */}
                    <div className="space-y-2">
                      <div className="w-12 h-1 bg-white/60"></div>
                      <div className="flex flex-wrap gap-2">
                        {app.tech.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="px-2 py-1 bg-white/10 border border-white/30 text-xs font-mono uppercase"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Bottom Accent */}
                    <div className="flex justify-between items-end pt-4">
                      <div className="flex gap-1">
                        <div className="w-3 h-3 bg-white"></div>
                        <div className="w-3 h-3 bg-white/60"></div>
                        <div className="w-3 h-3 bg-white/30"></div>
                      </div>
                      
                      {/* Link Indicator */}
                      <div className="group-hover:text-white text-white/50 transition-colors">
                        <div className="w-6 h-6 border-2 border-current flex items-center justify-center transform rotate-45">
                          <div className="w-2 h-2 bg-current transform -rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="h-24 md:hidden"></div>

        {/* Bottom Info */}
        <motion.div
          className="hidden md:block mt-12 lg:mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="inline-block bg-white/5 border-2 border-dashed border-white/30 p-4 lg:p-6">
            <p style={{ fontSize: 'clamp(0.75rem, 1vw, 1rem)' }} className="font-mono uppercase tracking-wider text-white/70">
              Click any project to explore
            </p>
            <div className="w-20 h-0.5 bg-white/30 mx-auto mt-3"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Portfolio; 